import { defaultSurfStyle } from "./defaultStyle.js";
export const SURF_DOC_TYPE = "surf.deck";
export const SURF_PLUGIN_ID = "surf";
export const SURF_DOC_SCHEMA_VERSION = 1;
export function newId() {
    return crypto.randomUUID();
}
export function isNormalized01(value) {
    return Number.isFinite(value) && value >= 0 && value <= 1;
}
export function validateNormalizedRect(rect, margin = 0) {
    const m = margin;
    if (!Number.isFinite(rect.x) || rect.x < -m - 1e-9 || rect.x > 1 + m + 1e-9) {
        return "x out of bounds";
    }
    if (!Number.isFinite(rect.y) || rect.y < -m - 1e-9 || rect.y > 1 + m + 1e-9) {
        return "y out of bounds";
    }
    if (!isNormalized01(rect.w))
        return "w must be in [0,1]";
    if (!isNormalized01(rect.h))
        return "h must be in [0,1]";
    if (rect.x + rect.w > 1 + m + 1e-9)
        return "x+w exceeds bounds";
    if (rect.y + rect.h > 1 + m + 1e-9)
        return "y+h exceeds bounds";
    return null;
}
export function validateLayerRectForEditor(rect) {
    return validateNormalizedRect(rect, 0.12);
}
export function validateLayer(layer) {
    const strictRectErr = validateNormalizedRect(layer);
    if (strictRectErr) {
        const editorRectErr = validateLayerRectForEditor(layer);
        if (editorRectErr)
            return strictRectErr;
    }
    if (!layer.id.trim())
        return "layer id required";
    return null;
}
export function validateDeckIds(deck) {
    const slideIds = new Set();
    for (const slide of deck.slides) {
        if (slideIds.has(slide.id))
            return `duplicate slide id ${slide.id}`;
        slideIds.add(slide.id);
        const layerIds = new Set();
        for (const layer of slide.layers) {
            if (layerIds.has(layer.id))
                return `duplicate layer id ${layer.id}`;
            layerIds.add(layer.id);
            const err = validateLayer(layer);
            if (err)
                return err;
        }
    }
    return null;
}
export function emptyDeck(title = "Presentazione senza titolo") {
    const now = new Date().toISOString();
    return {
        id: newId(),
        title,
        theme: defaultSurfStyle(),
        templates: [],
        slides: [],
        contentUpdatedAt: now,
    };
}
export function touchContentUpdatedAt(deck) {
    return { ...deck, contentUpdatedAt: new Date().toISOString() };
}
export function setDeckSourceContent(deck, text) {
    if ((deck.sourceContent ?? "") === text)
        return deck;
    return touchContentUpdatedAt({ ...deck, sourceContent: text });
}
/** Source markdown for Content tab and slide extraction (legacy decks fall back to excerpt). */
export function deckSourceContent(deck) {
    return deck.sourceContent ?? deck.deckContext?.sourceExcerpts?.join("\n\n") ?? "";
}
export function addSlide(deck, layout = "title-body") {
    const slide = {
        id: newId(),
        layout,
        background: { kind: "solid" },
        layers: [],
    };
    return touchContentUpdatedAt({
        ...deck,
        slides: [...deck.slides, slide],
    });
}
export function addLayer(deck, slideId, layer) {
    const err = validateNormalizedRect(layer);
    if (err)
        throw new Error(err);
    const nextLayer = { ...layer, id: layer.id ?? newId() };
    const layerErr = validateLayer(nextLayer);
    if (layerErr)
        throw new Error(layerErr);
    return touchContentUpdatedAt({
        ...deck,
        slides: deck.slides.map((slide) => slide.id === slideId
            ? { ...slide, layers: [...slide.layers, nextLayer] }
            : slide),
    });
}
export function reorderLayer(deck, slideId, layerId, newZ) {
    return touchContentUpdatedAt({
        ...deck,
        slides: deck.slides.map((slide) => {
            if (slide.id !== slideId)
                return slide;
            return {
                ...slide,
                layers: slide.layers.map((layer) => layer.id === layerId ? { ...layer, z: newZ } : layer),
            };
        }),
    });
}
export function renameDeck(deck, title) {
    return touchContentUpdatedAt({ ...deck, title: title.trim() || deck.title });
}
export function duplicateDeck(deck) {
    const now = new Date().toISOString();
    const idMap = new Map();
    const slides = deck.slides.map((slide) => {
        const newSlideId = newId();
        idMap.set(slide.id, newSlideId);
        return {
            ...slide,
            id: newSlideId,
            layers: slide.layers.map((layer) => {
                const newLayerId = newId();
                idMap.set(layer.id, newLayerId);
                return { ...layer, id: newLayerId };
            }),
        };
    });
    return {
        ...deck,
        id: newId(),
        title: `${deck.title} (copia)`,
        slides,
        contentUpdatedAt: now,
    };
}
export function parseDeckPayload(payloadJSON) {
    try {
        const parsed = JSON.parse(payloadJSON);
        if (!parsed?.id || !parsed.title || !Array.isArray(parsed.slides))
            return null;
        return parsed;
    }
    catch {
        return null;
    }
}
export function deckToPayloadJSON(deck) {
    return JSON.stringify(deck);
}
